import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';

import type { BaseFilters } from '@/types/filters';

/**
 * Props interface for the KycFiltersCard component
 */
interface CommonFiltersCardProps {
  /** Current filter values */
  filters: BaseFilters & {
    kam?: string;
    kycStatus?: string;
    accountStatus?: string;
    hasEmail?: string;
  };
  /** Whether any filters are currently active */
  hasActiveFilters: boolean;
  /** Whether the advanced filters are expanded */
  isExpanded: boolean;
  /** Callback for expand/collapse state changes */
  onExpandedChange: (expanded: boolean) => void;
  /** Callback for KYC status filter changes (applied immediately) - optional */
  onKycStatusChange?: (value: string) => void;
  /** Callback for Account status filter changes (applied immediately) - optional */
  onAccountStatusChange?: (value: string) => void;
  /** Callback for email availability filter changes (applied immediately) - optional */
  onHasEmailChange?: (value: string) => void;
  /** Callback for applying pending text filters */
  onApplyFilters: (pendingFilters: {
    search: string;
    companyType: string;
    industry: string;
    kam?: string;
  }) => void;
  /** Callback for clearing all filters */
  onClearFilters: () => void;
  /** Whether to show KAM filter */
  showKam?: boolean;
  /** Whether to show KYC status filter */
  showKycStatus?: boolean;
  /** Whether to show Account status filter */
  showAccountStatus?: boolean;
  /** Whether to show email availability filter */
  showHasEmail?: boolean;
}

const KNOWN_KYC_STATUSES = [
  'SEND_EMAIL',
  'RENEWAL',
  'EMAIL_SENT',
  'OFFLINE_PROCESS',
  'IN_REVIEW',
  'COMPLETED',
  'REJECTED',
] as const;

type KnownKycStatus = (typeof KNOWN_KYC_STATUSES)[number];

const KYC_STATUS_TO_FILTER_KEY: Record<KnownKycStatus, string> = {
  SEND_EMAIL: 'sendEmail',
  RENEWAL: 'renewal',
  EMAIL_SENT: 'emailSent',
  OFFLINE_PROCESS: 'offlineProcess',
  IN_REVIEW: 'inReview',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
};

export function CommonFiltersCard({
  filters,
  hasActiveFilters,
  isExpanded,
  onExpandedChange,
  onKycStatusChange,
  onAccountStatusChange,
  onHasEmailChange,
  onApplyFilters,
  onClearFilters,
  showKam = false,
  showKycStatus = false,
  showAccountStatus = false,
  showHasEmail = false,
}: CommonFiltersCardProps) {
  const { t, tDynamic } = useTranslation();

  // Local state for pending filter values
  const [pendingSearch, setPendingSearch] = useState(filters.search);
  const [pendingCompanyType, setPendingCompanyType] = useState(
    filters.companyType
  );
  const [pendingIndustry, setPendingIndustry] = useState(filters.industry);
  const [pendingKam, setPendingKam] = useState(filters.kam ?? '');

  // Sync pending values with actual filter values when they change externally
  useEffect(() => {
    // Intentional: reset local pending state when the controlled filters prop
    // changes externally. Behavior-preserving sync, not a render-phase set.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPendingSearch(filters.search);
    setPendingCompanyType(filters.companyType);
    setPendingIndustry(filters.industry);
    setPendingKam(filters.kam ?? '');
  }, [filters.search, filters.companyType, filters.industry, filters.kam]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleApplyFilters = useCallback(() => {
    onApplyFilters({
      search: pendingSearch,
      companyType: pendingCompanyType,
      industry: pendingIndustry,
      kam: pendingKam,
    });
  }, [
    pendingSearch,
    pendingCompanyType,
    pendingIndustry,
    pendingKam,
    onApplyFilters,
  ]);

  // Handle Enter key press
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleApplyFilters();
      }
    },
    [handleApplyFilters]
  );

  // Memoize clear field handlers
  const clearSearch = useCallback(() => {
    setPendingSearch('');
    onApplyFilters({
      search: '',
      companyType: pendingCompanyType,
      industry: pendingIndustry,
      kam: pendingKam,
    });
  }, [onApplyFilters, pendingCompanyType, pendingIndustry, pendingKam]);

  const clearCompanyType = useCallback(() => {
    setPendingCompanyType('');
    onApplyFilters({
      search: pendingSearch,
      companyType: '',
      industry: pendingIndustry,
      kam: pendingKam,
    });
  }, [onApplyFilters, pendingSearch, pendingIndustry, pendingKam]);

  const clearIndustry = useCallback(() => {
    setPendingIndustry('');
    onApplyFilters({
      search: pendingSearch,
      companyType: pendingCompanyType,
      industry: '',
      kam: pendingKam,
    });
  }, [onApplyFilters, pendingSearch, pendingCompanyType, pendingKam]);

  const clearKam = useCallback(() => {
    setPendingKam('');
    onApplyFilters({
      search: pendingSearch,
      companyType: pendingCompanyType,
      industry: pendingIndustry,
      kam: '',
    });
  }, [onApplyFilters, pendingSearch, pendingCompanyType, pendingIndustry]);

  const kycStatuses = useMemo(
    () => [
      { value: 'all', label: t('kyc.filters.options.statuses.all') },
      { value: 'null', label: t('kyc.filters.options.statuses.noRecord') },
      ...KNOWN_KYC_STATUSES.map(status => {
        const filterKey = `kyc.filters.options.statuses.${KYC_STATUS_TO_FILTER_KEY[status]}`;
        const filterTranslation = tDynamic(filterKey);

        if (filterTranslation !== filterKey) {
          return { value: status, label: filterTranslation };
        }

        // Fallback to general status translations
        const statusKey = `kyc.kycStatuses.${status}`;
        const statusTranslation = tDynamic(statusKey);

        return {
          value: status,
          label: statusTranslation !== statusKey ? statusTranslation : status,
        };
      }),
    ],
    [t, tDynamic]
  );

  const accountStatuses = useMemo(
    () => [
      {
        value: 'all',
        label: t('accountProcess.filters.options.accountProcessStatus.all'),
      },
      { value: 'SEND_EMAIL', label: t('kyc.kycStatuses.SEND_EMAIL') },
      { value: 'RENEWAL', label: t('kyc.kycStatuses.RENEWAL') },
      { value: 'EMAIL_SENT', label: t('kyc.kycStatuses.EMAIL_SENT') },
      {
        value: 'OFFLINE_PROCESS',
        label: t('kyc.kycStatuses.OFFLINE_PROCESS'),
      },
      { value: 'IN_REVIEW', label: t('kyc.kycStatuses.IN_REVIEW') },
      { value: 'COMPLETED', label: t('kyc.kycStatuses.COMPLETED') },
      { value: 'REJECTED', label: t('kyc.kycStatuses.REJECTED') },
    ],
    [t]
  );

  const emailOptions = useMemo(
    () => [
      { value: 'all', label: t('kyc.filters.options.emailAvailability.all') },
      {
        value: '1',
        label: t('kyc.filters.options.emailAvailability.withEmails'),
      },
      {
        value: '0',
        label: t('kyc.filters.options.emailAvailability.withoutEmails'),
      },
    ],
    [t]
  );

  const showCombinedKycAndEmailFilters =
    showKycStatus && showHasEmail && !!onKycStatusChange && !!onHasEmailChange;

  // Calculate the number of filter columns based on optional filters
  const filterColumns = useMemo(() => {
    let baseColumns = 3; // search, companyType, industry always present
    if (showKam) baseColumns++;
    if (showCombinedKycAndEmailFilters) {
      baseColumns++;
    } else {
      if (showKycStatus) baseColumns++;
      if (showHasEmail) baseColumns++;
    }
    if (showAccountStatus) baseColumns++;
    return Math.min(baseColumns, 4); // Max 4 columns
  }, [
    showKam,
    showCombinedKycAndEmailFilters,
    showKycStatus,
    showAccountStatus,
    showHasEmail,
  ]);

  return (
    <Card>
      {/* Conditional Header - Only show when expanded */}
      {isExpanded && (
        <CardHeader>
          <CardTitle className='flex items-center justify-between'>
            <span>{t('kyc.filters.title')}</span>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => onExpandedChange(false)}
              className='flex items-center gap-2'
            >
              {t('kyc.filters.advancedFilters')}
              <ChevronUp className='h-4 w-4' />
            </Button>
          </CardTitle>
        </CardHeader>
      )}

      <CardContent>
        {/* Collapsed view: Inline search + Advanced Filters button */}
        {!isExpanded && (
          <div className='flex items-center gap-3'>
            {/* Search Input - Inline */}
            <div className='relative flex-1'>
              <Input
                id='search'
                placeholder={t('kyc.filters.placeholders.searchCustomers')}
                value={pendingSearch}
                onChange={e => setPendingSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-describedby='search-help'
                className='pr-8'
              />
              {pendingSearch && (
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0'
                  onClick={clearSearch}
                  aria-label='Clear search'
                >
                  <X className='h-4 w-4' />
                </Button>
              )}
            </div>

            {/* Search Button - Apply filters */}
            <Button onClick={handleApplyFilters} className='px-4'>
              {t('kyc.filters.labels.search')}
            </Button>

            {/* Advanced Filters Button - Inline */}
            <Button
              type='button'
              variant='outline'
              onClick={() => onExpandedChange(true)}
              className='flex items-center gap-2 whitespace-nowrap relative'
            >
              {hasActiveFilters && (
                <Filter className='h-4 w-4 text-primary font-bold' />
              )}
              {t('kyc.filters.advancedFilters')}
              <ChevronDown className='h-4 w-4' />
            </Button>
          </div>
        )}

        {/* Expanded view: Full filters layout */}
        {isExpanded && (
          <div className='space-y-4'>
            {/* Search Input - Full width with label */}
            <div className='space-y-2'>
              <Label htmlFor='search'>{t('kyc.filters.labels.search')}</Label>
              <div className='relative'>
                <Input
                  id='search'
                  placeholder={t('kyc.filters.placeholders.searchCustomers')}
                  value={pendingSearch}
                  onChange={e => setPendingSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-describedby='search-help'
                  className='pr-8'
                />
                {pendingSearch && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0'
                    onClick={clearSearch}
                    aria-label='Clear search'
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
            </div>

            {/* Advanced Filters Grid */}
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${filterColumns} gap-4`}
            >
              {/* Company Type Filter */}
              <div className='space-y-2'>
                <Label htmlFor='company-type'>
                  {t('kyc.filters.labels.companyType')}
                </Label>
                <div className='relative'>
                  <Input
                    id='company-type'
                    placeholder={t('kyc.filters.placeholders.companyType')}
                    value={pendingCompanyType}
                    onChange={e => setPendingCompanyType(e.target.value)}
                    onKeyDown={handleKeyDown}
                    aria-label={t('kyc.filters.ariaLabels.companyTypeFilter')}
                    className='pr-8'
                  />
                  {pendingCompanyType && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0'
                      onClick={clearCompanyType}
                      aria-label='Clear company type'
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>

              {/* Industry Filter */}
              <div className='space-y-2'>
                <Label htmlFor='industry'>
                  {t('kyc.filters.labels.industry')}
                </Label>
                <div className='relative'>
                  <Input
                    id='industry'
                    placeholder={t('kyc.filters.placeholders.industry')}
                    value={pendingIndustry}
                    onChange={e => setPendingIndustry(e.target.value)}
                    onKeyDown={handleKeyDown}
                    aria-label={t('kyc.filters.ariaLabels.industryFilter')}
                    className='pr-8'
                  />
                  {pendingIndustry && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0'
                      onClick={clearIndustry}
                      aria-label='Clear industry'
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>

              {/* KAM Filter - Only show if enabled */}
              {showKam && (
                <div className='space-y-2'>
                  <Label htmlFor='kam'>{t('kyc.filters.labels.kam')}</Label>
                  <div className='relative'>
                    <Input
                      id='kam'
                      placeholder={t('kyc.filters.placeholders.kam')}
                      value={pendingKam}
                      onChange={e => setPendingKam(e.target.value)}
                      onKeyDown={handleKeyDown}
                      aria-label={t('kyc.filters.ariaLabels.kamFilter')}
                      className='pr-8'
                    />
                    {pendingKam && (
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0'
                        onClick={clearKam}
                        aria-label='Clear KAM'
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* KYC Status + Email Filter - Grouped container */}
              {showCombinedKycAndEmailFilters && (
                <div className='space-y-2'>
                  <div className='flex items-center gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='kyc-status'>
                        {t('kyc.filters.labels.kycStatus')}
                      </Label>
                      <Select
                        value={filters.kycStatus || 'all'}
                        onValueChange={onKycStatusChange}
                      >
                        <SelectTrigger
                          id='kyc-status'
                          aria-label={t(
                            'kyc.filters.ariaLabels.kycStatusFilter'
                          )}
                        >
                          <SelectValue
                            placeholder={t('kyc.filters.options.statuses.all')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {kycStatuses.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='has-email'>
                        {t('kyc.filters.labels.hasEmail')}
                      </Label>
                      <Select
                        value={filters.hasEmail || 'all'}
                        onValueChange={onHasEmailChange}
                      >
                        <SelectTrigger
                          id='has-email'
                          aria-label={t(
                            'kyc.filters.ariaLabels.emailAvailabilityFilter'
                          )}
                        >
                          <SelectValue
                            placeholder={t(
                              'kyc.filters.options.emailAvailability.all'
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {emailOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* KYC Status Filter - Only show if enabled */}
              {!showCombinedKycAndEmailFilters &&
                showKycStatus &&
                onKycStatusChange && (
                  <div className='space-y-2'>
                    <Label htmlFor='kyc-status'>
                      {t('kyc.filters.labels.kycStatus')}
                    </Label>
                    <Select
                      value={filters.kycStatus || 'all'}
                      onValueChange={onKycStatusChange}
                    >
                      <SelectTrigger
                        id='kyc-status'
                        aria-label={t('kyc.filters.ariaLabels.kycStatusFilter')}
                      >
                        <SelectValue
                          placeholder={t('kyc.filters.options.statuses.all')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {kycStatuses.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              {/* Account Status Filter - Only show if enabled */}
              {showAccountStatus && onAccountStatusChange && (
                <div className='space-y-2'>
                  <Label htmlFor='account-status'>
                    {t('accountProcess.filters.labels.accountProcessStatus')}
                  </Label>
                  <Select
                    value={filters.accountStatus || 'all'}
                    onValueChange={onAccountStatusChange}
                  >
                    <SelectTrigger id='account-status'>
                      <SelectValue placeholder='Select status...' />
                    </SelectTrigger>
                    <SelectContent>
                      {accountStatuses.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Has Email Filter - Only show if enabled */}
              {!showCombinedKycAndEmailFilters &&
                showHasEmail &&
                onHasEmailChange && (
                  <div className='space-y-2'>
                    <Label htmlFor='has-email'>
                      {t('kyc.filters.labels.hasEmail')}
                    </Label>
                    <Select
                      value={filters.hasEmail || 'all'}
                      onValueChange={onHasEmailChange}
                    >
                      <SelectTrigger
                        id='has-email'
                        aria-label={t(
                          'kyc.filters.ariaLabels.emailAvailabilityFilter'
                        )}
                      >
                        <SelectValue
                          placeholder={t(
                            'kyc.filters.options.emailAvailability.all'
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {emailOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
            </div>

            {/* Action buttons - Only visible when expanded */}
            <div className='flex items-center justify-between'>
              {/* Clear Filters button */}
              <Button
                variant='outline'
                size='sm'
                onClick={onClearFilters}
                disabled={!hasActiveFilters}
                aria-label={t('kyc.filters.ariaLabels.clearAllFilters')}
              >
                {t('kyc.filters.clearAll')}
              </Button>

              {/* Apply Filters Button */}
              <Button onClick={handleApplyFilters} className='px-6'>
                {t('kyc.filters.applyFilters')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
